<template>
  <div
    v-tooltip="'Map layers'"
    v-bind="$attrs"
  >
    <button
      :class="[
        'p-xs rounded-md text-default-500',
        (activeMapLayer !== 'default' || open) ? 'bg-primary-500 text-white' : 'hover:bg-default-200',
      ]"
      @click="toggleOpen(true)"
    >
      <span class="sr-only">
        Map layers
      </span>
      <svg
        aria-hidden="true"
        class="w-4 h-4"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.35714 7.125L0.875 9L4.35714 10.875M4.35714 7.125L9 9.625L13.6429 7.125M4.35714 7.125L0.875 5.25L9 0.875L17.125 5.25L13.6429 7.125M13.6429 7.125L17.125 9L13.6429 10.875M13.6429 10.875L17.125 12.75L9 17.125L0.875 12.75L4.35714 10.875M13.6429 10.875L9 13.375L4.35714 10.875"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
  <TransitionRoot
    appear
    :show="open === true"
    as="template"
  >
    <Dialog
      as="div"
      class="relative z-40"
      @close="toggleOpen(false)"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25" />
      </TransitionChild>
      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 translate-y-full"
            enter-to="opacity-100 translate-y-0"
            leave="transform duration-200 ease-in"
            leave-from="opacity-100 translate-y-0"
            leave-to="opacity-0 translate-y-full"
          >
            <DialogPanel class="w-full transform overflow-y-auto bg-white text-left align-middle shadow-xl transition-all h-96 p-large pb-huge">
              <div class="flex space-x-3 justify-end">
                <button
                  class="relative rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  @click="toggleOpen(false)"
                >
                  <span class="absolute -inset-2.5" />
                  <span class="sr-only">
                    Close panel
                  </span>
                  <svg
                    aria-hidden="true"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 18L18 6M6 6L18 18"
                      stroke="#6B7280"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <section class="-mt-large divide-y space-y-large">
                <article>
                  <RadioGroup
                    v-model="selectedMapStyle"
                    by="id"
                    class="space-y-large"
                    @update:model-value="handleStyleChange"
                  >
                    <RadioGroupLabel class="font-sans font-medium text-sm text-default-900">
                      Map type
                    </RadioGroupLabel>
                    <div class="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-base">
                      <div
                        v-for="style in MapboxStyleSources"
                        :key="style.id"
                        class="cols-span-1 text-center"
                      >
                        <RadioGroupOption
                          v-slot="{ checked }"
                          as="template"
                          :value="style"
                        >
                          <img
                            :src="style.image"
                            :class="[
                              'w-full cursor-pointer rounded-md text-center focus:outline-none ring-2 ring-offset-2 focus-visible:ring-primary-500',
                              checked ? 'ring-primary-500' : 'ring-transparent',
                            ]"
                            :alt="style.label"
                          >
                        </RadioGroupOption>
                        <span class="pt-2 block px-xs w-full text-xs text-default-700">
                          {{ style.label }}
                        </span>
                      </div>
                    </div>
                  </RadioGroup>
                </article>
                <article class="space-y-large">
                  <RadioGroup
                    v-model="selectedMapLayer"
                    class="space-y-large"
                    @update:model-value="handleLayerChange"
                  >
                    <RadioGroupLabel class="block font-sans font-medium text-sm text-default-900 mt-large">
                      Map layer
                    </RadioGroupLabel>
                    <div class="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-base">
                      <div
                        v-for="style in MapboxLayerSources"
                        :key="style.id"
                        class="cols-span-1 text-center"
                      >
                        <RadioGroupOption
                          v-slot="{ checked }"
                          as="template"
                          :value="style.id"
                        >
                          <img
                            :src="style.image"
                            :class="[
                              'w-full cursor-pointer rounded-md text-center focus:outline-none ring-2 ring-offset-2 focus-visible:ring-primary-500',
                              checked ? 'ring-primary-500' : 'ring-transparent',
                            ]"
                            :alt="style.label"
                          >
                        </RadioGroupOption>
                        <span class="pt-2 block px-xs w-full text-xs text-default-700">
                          {{ style.label }}
                        </span>
                      </div>
                    </div>
                  </RadioGroup>
                </article>
              </section>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import { useToggle } from '@vueuse/core';
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel, RadioGroupOption, RadioGroupLabel, RadioGroup,
} from '@headlessui/vue';
import MapboxLayerSources from '@/Config/MapboxLayerSources';
import MapboxStyleSources from '@/Config/MapStyleSources';
import { type MapLayer, MapSource } from '@/Stores/Map';
import { ref, watch } from 'vue';

const [open, toggleOpen] = useToggle<boolean>(false);

const props = defineProps<{
  activeMapStyle: MapSource;
  activeMapLayer: MapLayer;
}>();

const selectedMapStyle = ref<MapSource>(props.activeMapStyle);
const selectedMapLayer = ref<MapLayer>(props.activeMapLayer);

const emit = defineEmits(['on-style-change', 'on-layer-change']);

watch(() => props.activeMapStyle, (newValue) => {
  selectedMapStyle.value = newValue;
});

watch(() => props.activeMapLayer, (newValue) => {
  selectedMapLayer.value = newValue;
});

const handleLayerChange = (newValue: MapLayer) => {
  emit('on-layer-change', newValue);
};

const handleStyleChange = (newValue: MapSource) => {
  emit('on-style-change', newValue);
};
</script>
